import type { DateTime } from "luxon";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDepartmentFlags } from "../../../hooks/useDepartmentFlags";
import { NursePanelHeader } from "../../../utils/panelComponents";
import { formatShiftDate } from "../../AddNurse/addStaffUtils";
import type { AddStaffDate } from "../../AddNurse/useAddStaffDate";
import { MessageWorkingFilterEnum } from "../../StaffCommunication/util/Broadcast/MessageFilter";
import { NewBroadcastMessageModal } from "../../StaffCommunication/util/Broadcast/NewBroadcastMessageModal";

const HEADER_SX = { px: 3, pt: 2.5, pb: 1.25 } as const;

/** The overstaffed message's "who works" window: a staffing day from 6 AM to 5:59 AM the next morning, as before. */
const BROADCAST_DAY_START_HOUR = 6;

/** "We're overstaffed — would you skip this shift?" to everyone working the date: the message icon's shortcut. */
const OverstaffedBroadcast: FC<{ staffingDate: DateTime; onClose: () => void }> = ({ staffingDate, onClose }) => {
	const { t } = useTranslation();
	const { timezone } = useDepartmentFlags();
	const day = staffingDate.setZone(timezone);
	const start = day.startOf("day").set({ hour: BROADCAST_DAY_START_HOUR });
	return (
		<NewBroadcastMessageModal
			initialSmsMessage={t("sms.overstaffed.initial", { date: day.toFormat("EEE, MMM dd") })}
			defaultWorkingFilter={MessageWorkingFilterEnum.STAFF_THAT_WORKS}
			initialFilters={[
				{
					workFilter: MessageWorkingFilterEnum.STAFF_THAT_WORKS,
					dateFilter: day,
					timeFilter: { start, end: start.plus({ days: 1 }).minus({ minutes: 1 }) },
				},
			]}
			onClose={onClose}
		/>
	);
};

/** "Remove staff" and the date it removes from — a date picker when the caller lets the tab move days. */
export const RemoveStaffHeader: FC<{ staffingDate: DateTime; datePicker: Pick<AddStaffDate, "canChangeDate" | "changeDate" | "minDate" | "maxDate"> }> = ({
	staffingDate,
	datePicker,
}) => {
	const { t } = useTranslation();
	const [isBroadcastOpen, setIsBroadcastOpen] = useState(false);
	return (
		<>
			<NursePanelHeader
				date={staffingDate}
				title={t("remove.staff.title")}
				titleCss={HEADER_SX}
				changeDate={datePicker.canChangeDate}
				onSelectedDateChanged={datePicker.changeDate}
				minSelectableDate={datePicker.minDate}
				maxSelectableDate={datePicker.maxDate}
				dateAriaLabel={t("remove.staff.date.change", { date: formatShiftDate(staffingDate) })}
				dateDataTestId="remove-staff-date-button"
				onMessageIconClick={() => setIsBroadcastOpen((open) => !open)}
			/>
			{isBroadcastOpen && <OverstaffedBroadcast staffingDate={staffingDate} onClose={() => setIsBroadcastOpen(false)} />}
		</>
	);
};
